<template>
    <b-card header-tag="header" footer-tag="footer" class="w-100 h-100"
        header-class="d-flex justify-content-between border-bottom py-2 bg-white"
        body-class="bg-white overflow-auto">
        <template #header>
            <h5 class="my-1">
                {{$t('CertificatePreview')}} {{ Certificate ? Certificate.CertificateName : '' }}
            </h5>
        </template>
        <template #default>
            <b-col v-if="Certificate" class="overflow-auto" style="max-height: 75vh;">
                <b-row v-if="displayedInformations.includes(sMADisplayInformationEnum.General)" class="mb-3">
                    <b-col class="p-0 pr-1">
                        <!-- AFFICHAGE INFORMATIONS GENERAL DU MANDAT -->
                        <InfoCard
                            :Fields="Certificate.GetGeneralInformations()">
                            <template #bottom-action>
                                <b-button v-if="Forms[Certificate.Distributor]?.CanRenew && Certificate.Status >= certificateStateEnum.SupervisorValidated" @click="OnRenewCertificate()" pill :variant="'outline-' + Variant" class="m-1">
                                    {{$t('RenewCertificate')}}
                                </b-button>
                            </template>
                        </InfoCard>
                    </b-col>
                </b-row>
                <b-row v-if="displayedInformations.includes(sMADisplayInformationEnum.Meters)" class="mb-3">
                    <!-- AFFICHAGE INFORMATIONS COMPTEURS -->
                    <InfoCard
                        TitleKey="MetersInformations"
                        IconName="b-icon-diagram3"
                        :Fields="Certificate.GetMetersInformations()">
                        <template #top-action>
                            <b-button @click="DisplayMeters()" :variant="'outline-' + Variant" pill>
                                <font-awesome-icon icon="fa-solid fa-eye" />
                                {{ $t('Visualize') }}
                            </b-button>
                        </template>
                    </InfoCard>
                </b-row>
                <b-row v-if="displayedInformations.includes(sMADisplayInformationEnum.Customer)" class="mb-3">
                    <!-- AFFICHAGE INFORMATIONS PARTICULIER -->
                    <InfoCard
                        TitleKey="CustomerInformation"
                        IconName="b-icon-person-fill"
                        :Fields="Certificate.GetParticularInformations()"/>
                </b-row>
                <b-row v-if="displayedInformations.includes(sMADisplayInformationEnum.Customer) && Certificate.CustomerInfo instanceof ProfessionalCustomerInformation" class="mb-2">
                    <!-- AFFICHAGE INFORMATIONS PROFESSIONNEL -->
                    <InfoCard
                        TitleKey="CompanyInformations"
                        IconName="b-icon-building"
                        :Fields="Certificate.GetProfessionalInformations()"/>
                </b-row>
                <b-row v-if="displayedInformations.includes(sMADisplayInformationEnum.Historic)" class="mb-3">
                    <!-- AFFICHAGE INFORMATIONS HISTORIQUE MANDAT -->
                    <InfoCard
                        TitleKey="Historic"
                        IconName="b-icon-clock"
                        :Fields="Certificate.GetHistoricInformations()"/>
                </b-row>
                <b-row v-if="displayedInformations.includes(sMADisplayInformationEnum.Appendixes)" class="mb-3">
                    <!-- AFFICHAGE ANNEXES Du MANDAT -->
                    <ResourceCard
                        TitleKey="Appendixes"
                        IconName="b-icon-folder"
                        :Resources="Certificate.Resources.filter(p => p.Type == '7')">
                        <template #top-action>
                            <b-button 
                                @click="OnDownloadAll" 
                                :disabled="Certificate.Resources.length == 0"
                                :variant="'outline-' + Variant" 
                                pill 
                                v-b-tooltip.hover 
                                :title="$t('DownloadAll')">
                                <font-awesome-icon icon="fa-solid fa-download" class="pr-2"/>
                                <font-awesome-icon icon="fa-solid fa-folder-open" />
                            </b-button>
                        </template>
                        <template #left-resource-action="{ resource }">
                            <b-button v-if="Certificate.Status < certificateStateEnum.SupervisorValidated" @click="OnDeleteAppendix(resource)" :variant="'outline-' + Variant" class="btn-circle border-0" v-b-tooltip.hover :title="$t('Delete')">
                                <font-awesome-icon icon="fa-solid fa-xmark"/>
                            </b-button>
                        </template>
                        <template #right-resource-action="{ resource }">
                            <b-button @click="OnDownloadAppendix(resource)" :variant="'outline-' + Variant" class="btn-circle" v-b-tooltip.hover :title="$t('Download')">
                                <font-awesome-icon icon="fa-solid fa-download" />
                            </b-button>
                        </template>
                    </ResourceCard>
                </b-row>
                <!-- AFFICHAGE LISTE DES COMPTEURS -->
                <b-modal
                    v-if="displayMeters"
                    @hide="displayMeters = false"
                    size="xl" 
                    :visible="displayMeters" 
                    :hide-footer="true" 
                    dialog-class="fullscreen-modal" 
                    content-class="fullscreen-modal"  
                    body-class="d-flex flex-column mh-100 h-100 no-gutters p-0 w-100 overflow-hidden"
                    :title="$t('MetersList')">
                    <MetersList 
                        @RenewSubscribtionEvent="OnRenewSubscribtion"
                        @RefreshEvent="OnRefreshMeter"
                        :Certificate="Certificate"
                        Mode="Display"/>
                </b-modal>
            </b-col>
            <!-- AFFICHAGE PAR DEFAUT SI PAS DE MANDAT SELECTIONNE -->
            <b-card-text v-else text-tag="h6" class="h-100 d-flex justify-content-center align-items-center">
                {{$t('SelectACertificate')}}
            </b-card-text>
        </template>
    </b-card>
</template>
<script lang="ts">
    /**
     * Composants Vue js
     */
    import Component, { mixins } from 'vue-class-component';
    import { InjectReactive, Prop, Vue } from 'vue-property-decorator';

    /**
     * Traductions
     */
    import locale from '../locales/SMA.json';

    /**
     * Classes et Enumérations
     */
    import { Resource, SMACertificate } from '@/types/Administration/SMA/SMACertificate';
    import ContractTypeEnum from '@/enums/Administration/SMA/ContractTypeEnum';
    import { ProfessionalCustomerInformation as ProfessionalCustomerInformation } from '@/types/Administration/SMA/CustomerInfo';
    import CustomerTypeEnum from '@/enums/Administration/SMA/CustomerTypeEnum';
    import CertificateStateEnum from '@/enums/Administration/SMA/CertificateStateEnum';
import SMADisplayInformationEnum from '@/enums/Administration/SMA/SMADisplayInformationEnum';
    import { SMADistributorForm } from '@/types/Administration/SMA/SMADistributorForm';
    import { Dictionary } from 'vue-router/types/router';

    /**
     * Composants d'affichage informations mandat
     */
    import InfoCard from './CertificateDisplay/InfoCard.vue';
    import ResourceCard from './CertificateDisplay/ResourceCard.vue';
    
    /**
     * Composants d'affichage informations compteurs
     */
    import MetersList from './DistributorForm/MetersList.vue';
    import { SMAUtils } from './SMAUtils';
import DownloadStateEnum from '@/enums/DownloadStateEnum';
import Download from '@/types/Download';
    
    @Component({
        components: {
            InfoCard,
            ResourceCard,
            MetersList
        },
        i18n: {messages: locale}
    })
    /**
     * Composant d'affichage d'un mandat distributeur
     */
    export default class CertificateDisplay extends mixins (Vue, SMAUtils) {
        
        /**
         * Mandat à afficher
         */
        @Prop()
        public Certificate!: SMACertificate;

        /**
         * Formulaires distributeurs
         */
        @Prop()
        public Forms!: Dictionary<SMADistributorForm>;

        /** 
         * Variant de couleur à appliquer sur le composant 
         */
        @InjectReactive()
        public Variant!: string;

        /**
         * Affichage fenêtre compteurs
         */
        private displayMeters: boolean = false;

        /**
         * Sections d'informations affichées dans la prévisualisation du mandat
         * Décrites côté serveur dans SMAAttribute (DisplayedInformations)
         * Défaut : toutes les sections
         */
        private get displayedInformations(): Array<SMADisplayInformationEnum>
        {
            return this.Forms[this.Certificate.Distributor]?.DisplayedInformations
                ?? [SMADisplayInformationEnum.General, SMADisplayInformationEnum.Meters, SMADisplayInformationEnum.Customer, SMADisplayInformationEnum.Historic, SMADisplayInformationEnum.Appendixes];
        }

        /**
         * Enumérations mandat
         */
        public contractTypeEnum = ContractTypeEnum;
        public sMACustomerTypeEnum = CustomerTypeEnum;
        public certificateStateEnum = CertificateStateEnum;
        public sMADisplayInformationEnum = SMADisplayInformationEnum;

        /**
         * Type Classe de données client
         */
        ProfessionalCustomerInformation = ProfessionalCustomerInformation;

        /**
         * Affichage fenêtre des compteurs
         */
        private DisplayMeters() : void
        {
            this.displayMeters = true;
        }

        /**
         * Gestion de l'événement de renouvellement d'abonnement
         * Pas encore implémenté
         * @param id Identifiant compteur
         */
        private OnRenewSubscribtion(id: string): void
        {
            this.$emit('RenewSubscribtionEvent', this.Certificate, id);
        }

        /**
         * Rafraichissement des données du compteur
         * Utilisé si une information du compteur est incorrect (Exemple : RAE, Adresse, Energie)
         * Ce méthode va mettre à jour les informations du compteur suivant les données de la fiche objet
         * @param Id Identifiant du compteur
         */
        private OnRefreshMeter(Id: string) : void
        {
            this.$emit('OnRefreshMeter', Id);
        }

        /**
         * Gestion de l'événement de renouvellement du mandat
         * @param id Identifiant mandat
         */
        private OnRenewCertificate(): void
        {
            this.$emit('RenewCertificateEvent', this.Certificate);
        }

        /**
         * Gestion de l'événement de téléchargement d'une annexe
          * @param resource Ressources à télécharger
          * @returns void
         */
        private OnDownloadAppendix(resource: Resource): void
        {
            this.$emit('DownloadResource', this.Certificate, resource);
        }

        /**
         * Télécargement de toutes les annexes ainsi que le mandat au format zip
         */
        private async OnDownloadAll(): Promise<void>
        {
            let index: number = this.$store.getters['DownloadingStore/GetLastIndex'];
            let download: Download = {
                Index: ++index, 
                Name: this.Certificate.Name + ".zip", 
                Type: ".zip", 
                State: DownloadStateEnum.InProgress, 
                Data: "", 
                Direct: true
            }

            this.$store.dispatch('DownloadingStore/AddDownloading', download);
            
            console.debug("Downloading all resources for certificate: ", this.Certificate);

            await this.$store.dispatch("CertificateStore/DownloadAll", this.Certificate).then((response) => {
                this.$store.dispatch('DownloadingStore/AddData', {index, data: response.data});
                this.$store.dispatch('DownloadingStore/UpdateState', {index, state: DownloadStateEnum.Finished});
            });
        }

        /**
         * Gestion de l'événement de suppression d'une annexe
          * @param resource Ressource à supprimer
          * @returns void
         */
        private OnDeleteAppendix(resource: Resource): void
        {
            this.showBoxConfirm(this.$t('PleaseConfirm').toString(), this.Variant, 'DeleteResourceConfirm').then((value) => 
            {
                if(value)
                    this.$emit('DeleteResource', this.Certificate, resource);
            });
        }
    }

</script>
<style lang="css">

	:deep(.fullscreen-modal) {
		height: 100% !important;
		width: 100% !important;
		max-width: 100% !important;
		max-height: var(--fullHeight) !important;
		margin: 0 !important;
		overflow: hide !important;
		padding: 1vh !important;
	}

</style>